import { createSlice } from '@reduxjs/toolkit';

import { initialOrganization } from '../../components/Organizations/constants/initialStates';
import { REQUEST } from '../../constants/request';
import { INITIAL_LOADING_STATE } from '../../utils/asyncThunkRequest';

import { createOrganizationExtraReducers } from './asyncThunk/createOrganization';
import { fetchAllOrganizationsExtraReducers } from './asyncThunk/fetchAllOrganizations';
import { fetchOrganizationHovercardExtraReducers } from './asyncThunk/fetchOrganizationHovercard';
import { updateOrganizationExtraReducers } from './asyncThunk/updateOrganization';
import { setArchivedOrganizationExtraReducers } from './asyncThunk/setArchivedOrganization';
import { setDeactivatedOrganizationExtraReducers } from './asyncThunk/setDeactivatedOrganization';

export { createOrganization } from './asyncThunk/createOrganization';
export { fetchAllOrganizations } from './asyncThunk/fetchAllOrganizations';
export { fetchOrganizationHovercard } from './asyncThunk/fetchOrganizationHovercard';
export { updateOrganization } from './asyncThunk/updateOrganization';
export { setArchivedOrganization } from './asyncThunk/setArchivedOrganization';
export { setDeactivatedOrganization } from './asyncThunk/setDeactivatedOrganization';

const INITIAL_STATE = {
  organizations: {
    organization: {
      byId: {},
      allIds: [],
      request: {},
      actions: {
        create: {
          request: {},
        },
        update: {
          request: {},
        },
        archive: {
          request: {},
        },
        deactivate: {
          request: {},
        },
      },
    },
    allOrganizations: {
      layout: [],
      request: { ...INITIAL_LOADING_STATE },
    },
  },
};

export const OrganizationsSlice = createSlice({
  name: 'organizations',
  initialState: INITIAL_STATE,
  reducers: {
    resetOrganizationsSlice: () => INITIAL_STATE,
    resetOrganizationHovercardRequest: (state, { payload }) => {
      delete state.organizations.organization.request[payload.path.orgId];
    },
    resetAllOrganizations: state => {
      state.organizations.allOrganizations = {
        layout: [],
        request: { ...INITIAL_LOADING_STATE },
      };
    },
  },
  extraReducers: builder => {
    createOrganizationExtraReducers(builder);
    fetchAllOrganizationsExtraReducers(builder);
    fetchOrganizationHovercardExtraReducers(builder);
    updateOrganizationExtraReducers(builder);
    setArchivedOrganizationExtraReducers(builder);
    setDeactivatedOrganizationExtraReducers(builder);
  },
});

export const {
  resetOrganizationsSlice,
  resetOrganizationHovercardRequest,
  resetAllOrganizations,
} = OrganizationsSlice.actions;

export default OrganizationsSlice.reducer;

export const selectorGetOrganization = (state, orgId) =>
  state.organizations.organizations.organization.byId[orgId] ||
  initialOrganization;
export const selectorGetOrganizationAllIds = state =>
  state.organizations.organizations.organization.allIds;
export const selectorGetOrganizationRequest = (state, orgId) =>
  state.organizations.organizations.organization.request[orgId] || {
    ...INITIAL_LOADING_STATE,
  };
export const selectorHasOrgLoadingState = (state, orgId) => {
  const { request, actions } = state.organizations.organizations.organization;

  return (
    request[orgId]?.loading === REQUEST.pending ||
    actions.create.request[orgId]?.loading === REQUEST.pending ||
    actions.update.request[orgId]?.loading === REQUEST.pending ||
    !selectorGetOrganizationAllIds(state).includes(orgId)
  );
};
export const selectorAllOrganizations = state =>
  state.organizations.organizations.allOrganizations.layout || [];
export const selectorAllOrganizationsLoadingState = state =>
  state.organizations.organizations.allOrganizations.request.loading ||
  REQUEST.idle;
export const selectorAllOrganizationsError = state =>
  state.organizations.organizations.allOrganizations.request.error;
export const selectorGetOrganizationProfilePicture = (state, orgId) =>
  state.organizations.organizations.organization.byId[orgId]?.metadata
    ?.profilePicUrl;
